import StringToColorConverter from '../../../util/StringToColorConverter.js';
import {nicelyRounded} from '../../../util/CommonUtils.js'

export class AbstractScale {
	constructor() {
		this._scale = null;
	}

	get range() {
		return this._scale.range();
	}

	set range(range) {
		this._scale.range(range);
		this._rangeStart = range[0];
	}

	get domain() {
		return this._scale.domain();
	}

	set domain(domain) {
		this._scale.domain(domain);
	}

	get d3Scale() {
		return this._scale;
	}

	fromPx(px) {
		return this._scale.invert(px);
	}

	toPx(val) {
		return this._scale(val);
	}

	/**
	 * @param {number} px
	 * @returns {number}
	 */
	lengthFromPx(px) {
		if (this._rangeStart === undefined)
			this._rangeStart = this.range[0];
		return Math.abs(px - this._rangeStart);
	}
}

export class LinearScale extends AbstractScale {
	/**
	 * @param {{domain:number[], range:number[], padding?:number}} opts
	 */
	constructor(opts) {
		super();
		opts = Object.assign({domain: [], range: [], padding: 0}, opts)
		const domain = d3.extent(opts.domain)
		this._padding = opts.padding;
		this._scale = d3.scaleLinear().range(opts.range).domain(this.#domainWithPadding(domain, opts.padding));
	}

	getTicks(axisLength) {
		let max = Math.max(...this._scale.ticks(20).map(t => this.getFormat()(t)).map(el => el.length)) * 6;
		let count = Math.min(20, axisLength / (max * 2));
		return this._scale.ticks(count);
	}

	getFormat() {
		return nicelyRounded
	}

	toPx(val) {
		if (Number.isFinite(val))
			return this._scale(val)
		return undefined;
	}

	get domain(){
		return super.domain;
	}

	set domain(domain){
		super.domain = this.#domainWithPadding(domain, this._padding);
	}

	#domainWithPadding(domain, padding){
		const step = ((domain[1] - domain[0]) || domain[0] || 1) * padding;
		return [domain[0] - step, domain[1] + step];
	}
}

export class OrdinalScale extends AbstractScale {
	/**
	 * @param {{domain:Iterable, range:number[], padding?:number}} opts
	 */
	constructor(opts) {
		super();
		opts = this._opts = Object.assign({domain: [], range: [], padding: 0}, opts)
		this._scale = d3.scalePoint()
			.range(opts.range)
			.domain(OrdinalScale.normalizeAndSort(opts.domain));
		this._charsPerTick = 0;
		this._updatePadding();
	}

	getTicks(axisLength) {
		this._charsPerTick = 0;
		let maxTickPixels = Math.max(...this.domain.map(el => el ? el.length : 5)) * 7 || 1;
		let maxTicks = Math.floor(axisLength / maxTickPixels);
		if (this.domain.length <= maxTicks)
			return this.domain;
		let freeSpace = axisLength - (this.domain.length + 1)/*gaps*/ * 12/*gap width, px*/;
		this._charsPerTick = Math.ceil(freeSpace / Math.max(1, this.domain.length * 6));
		if (this._charsPerTick >= 2)
			return this.domain
		this._charsPerTick = 2;
		let totalTicks = Math.max(1, axisLength / 28);
		let step = Math.floor(this.domain.length / totalTicks);
		const ticks = [];
		for (let i = 0; i < this.domain.length; i += step) {
			ticks.push(this.domain[i])
		}
		return ticks
	}

	getFormat() {
		return (tick) => {
			tick = OrdinalScale.normalizeValue(tick) || "Empty";
			if (this._charsPerTick < 2)
				return tick;
			return tick.substr(Math.max(0, tick.length - this._charsPerTick), this._charsPerTick + 1)
		}
	}

	get range(){
		return super.range;
	}

	set range(r){
		const response = super.range = r;
		this._updatePadding();
		return response;
	}

	_updatePadding(){
		let valuesPerTick = this.domain.length / this.getTicks(Math.max(...this.range)).length;
		this._scale.padding(valuesPerTick * this._opts.padding);
	}

	toPx(val) {
		return super.toPx(OrdinalScale.normalizeValue(val))
	}

	static normalizeAndSort(values) {
		return [...new Set(values)]
			.map(val => OrdinalScale.normalizeValue(val))
			.sort(OrdinalScale.VALUE_COMPARATOR)
	}

	/**
	 * Returns value normalized to the scale:
	 *	 null 		=> null
	 *	 undefined 	=> null
	 *	 ""			=> null
	 *	 NaN		=> null
	 *	 Infinity	=> null
	 *	 -1			=> "-1"
	 *	 0			=> "0"
	 *	 1.02		=> "1.02"
	 *	 " abc "	=> "abc"
	 *	 true		=> "true"
	 *	 false		=> "false"
	 */
	static normalizeValue(val) {
		if (typeof val === "string")
			val = val.trim()
		if (OrdinalScale.NULLABLE_VALUES.has(val))
			return null;
		return "" + val
	}

	/**
	 * Values which we treat as null
	 */
	static NULLABLE_VALUES = new Set([null, undefined, "", NaN, -Infinity, Infinity]);

	static VALUE_COMPARATOR = (a, b) => {
		return a === null ? -1 : b === null ? 1 : a < b ? -1 : a > b ? 1 : 0
	}
}

export class ScaleBuilder {
	static computeColorScale(type, values) {
		if (type === "number") {
			const linearScale = new LinearScale({range: [0.1, 0.8], domain: d3.extent(values)});
			return (val) => {
				return Number.isFinite(val) ? d3.interpolateBlues(linearScale.toPx(val)) : '#bab0ab'
			};
		} else {
			const ordinalScale = d3.scaleOrdinal()
				.range(StringToColorConverter.palette)
				.domain(OrdinalScale.normalizeAndSort(values));
			return (val) => {
				val = OrdinalScale.normalizeValue(val)
				return val ? ordinalScale(val) : '#bab0ab'
			};
		}
	}

	static computeSizeScale(type, values, range = [3, 12]) {
		if (type === "number") {
			let min = Number.POSITIVE_INFINITY, max = Number.NEGATIVE_INFINITY;
			for (const next of values)
				if (Number.isFinite(next)) {
					min = Math.min(min, next);
					max = Math.max(max, next);
				}
			range = numbersRangeToPixelsRange({min:min, max:max}, {min: range[0], max: range[1]})
			return new LinearScale({range: range, domain: [min, max]});
		}
		let strings = new Set();
		for (const next of values) {
			let str = OrdinalScale.normalizeValue(next);
			if (str != null)
				strings.add(str)
		}
		strings = Array.from(strings);
		if (strings.length < 2)
			range[1] = range[0]
		return new OrdinalScale({domain: strings, range: range, padding: 0});
	}
}

/**
 * @param {{min:number, max:number}} rangeNumbers
 * @param {{min:number, max:number}} rangePixels
 */
export const numbersRangeToPixelsRange = (rangeNumbers, rangePixels) => {
	const {min, max} = rangeNumbers;
	if (min === max)
		return [rangePixels.min, rangePixels.min]
	let diffTimes;
	if (min === 0 || max === 0)
		diffTimes = Math.max(min, max)
	else
		diffTimes = (max - min) / Math.min(Math.abs(min), Math.abs(max));
	return [rangePixels.min, Math.min(rangePixels.max, rangePixels.min + rangePixels.min * diffTimes)]
}
